// Importing React libs
import { useEffect, useState } from 'react';

// Importing helix modules
import { chromosomes, MChromosomeDictionary } from 'helix-modules/chromosome';

/**
 *
 * @param {"hg19" | "hg38"} assembly
 */
const fetchChromosomeDictionary = async (assembly) => {
  const chromPositions = {};
  const dictionary = await MChromosomeDictionary.load({
    bandsUrl: `/bioinfo/${assembly}_cytobands.tsv`,
    dictUrl: `/bioinfo/${assembly}_fasta.dict`,
  });
  let prevEnd = 0;

  for (const element of dictionary) {
    const { label, length, cytobands } = element;

    const chromStart = 0;
    const chromEnd = length;
    const genomeStart = prevEnd;
    const genomeEnd = prevEnd + length;

    chromPositions[label] = {
      center: (chromStart + chromEnd) / 2,
      end: chromEnd,
      genomeStart: genomeStart,
      genomeCenter: (genomeStart + genomeEnd) / 2,
      genomeEnd: genomeEnd,
      length,
      start: chromStart,
    };

    const bands = {};

    for (const cytoband of cytobands) {
      const { band, chromStart: bandStart, chromEnd: bandEnd } = cytoband;
      const bandLength = bandEnd - bandStart;
      const bandGenomeStart = genomeStart + bandStart;
      const bandGenomeEnd = genomeStart + bandLength;
      bands[band] = {
        center: bandLength / 2,
        end: bandEnd,
        genomeStart: bandGenomeStart,
        genomeCenter: (bandGenomeStart + bandGenomeEnd) / 2,
        genomeEnd: bandGenomeEnd,
        length: bandLength,
        start: bandStart,
      };
    }

    chromPositions[label].bands = bands;

    prevEnd += length;
  }

  return { chromPositions, genomeLength: prevEnd };
};

/**
 *
 * @param {"hg19" | "hg38"} assembly which positioning scheme to use
 * @returns {Object} data that is helpful in genome plots
 */
const useGenomePositions = (assembly = 'hg38') => {
  const [isLoading, setIsLoading] = useState(true);
  const [genomeLength, setGenomeLength] = useState(0);
  const [chromPositions, setChromPositions] = useState({});

  useEffect(() => {
    fetchChromosomeDictionary(assembly)
      .then(res => {
        setChromPositions(res.chromPositions);
        setGenomeLength(res.genomeLength);
        setIsLoading(false);
      });
  }, [assembly]);

  const chroms = chromosomes.map(chromosome => chromosome.label);
  const genomeExtent = [0, genomeLength];

  return {
    chroms,
    chromPositions,
    genomeExtent,
    genomeLength,
    isLoading,
  };
};

export default useGenomePositions;